import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ConsentCollection from './pages/ConsentCollection';
import Login from './pages/Login';
import TenantAuthPage from './pages/TenantAuthPage';
import OrganizationSignupPage from './pages/OrganizationSignupPage';
import AcceptInvitation from './pages/AcceptInvitation';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { Templates } from './pages/Templates';
import { ConsentRecords } from './pages/ConsentRecords';
import { DataCollectors } from './pages/DataCollectors';
import { Projects } from './pages/Projects';
import { Analytics } from './pages/Analytics';
import { ConsentForms } from './pages/ConsentForms';
import { TenantProfile } from './pages/TenantProfile';
import { UserProfile } from './pages/UserProfile';
import { SignupRequests } from './pages/SignupRequests';
import { UserManagement } from './pages/UserManagement';
import { AuditLogs } from './pages/AuditLogs';
import { WithdrawalRequests } from './pages/WithdrawalRequests';
import { HelpSupport } from './pages/HelpSupport';
import TemplateLibrary from './pages/TemplateLibrary';
import Regions from './pages/Regions';
import DataErasure from './pages/DataErasure';
import NotificationTemplateEditor from './pages/NotificationTemplateEditor';
import HomePage from './pages/HomePage';
import ProtectedRoute from './components/ProtectedRoute';
import { OnboardingProvider } from './components/OnboardingTour';
import { RegionProvider } from './contexts/RegionContext';
import { I18nProvider } from './i18n';
// Data Owner Portal imports
import {
  DataOwnerPortalLogin,
  DataOwnerConsentHistory,
  DataOwnerWithdrawalForm,
  DataOwnerWithdrawalHistory,
  DataOwnerHelp,
  PortalTenantSelector,
} from './pages/portal';

// Wrapper component to include onboarding tour and region context for admin routes
const AdminRouteWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <RegionProvider>
      <OnboardingProvider>
        {children}
      </OnboardingProvider>
    </RegionProvider>
  );
};

function App() {
  return (
    <I18nProvider defaultLanguage="en">
      <Router>
        <Routes>
          {/* Public Consent Collection Route */}
          <Route path="/consent/:tenantId/:formId" element={<ConsentCollection />} />
          
          {/* Multi-tenant Authentication Routes */}
          <Route path="/t/:tenantSlug/login" element={<TenantAuthPage />} />
          <Route path="/t/:tenantSlug/forgot-password" element={<ForgotPassword />} />
          <Route path="/t/:tenantSlug/reset-password" element={<ResetPassword />} />
          
          {/* Data Owner Portal Routes - Self-service for data subjects */}
          <Route path="/my-consent" element={<PortalTenantSelector />} />
          <Route path="/t/:tenantSlug/my-consent/login" element={<DataOwnerPortalLogin />} />
          <Route path="/t/:tenantSlug/my-consent/history" element={<DataOwnerConsentHistory />} />
          <Route path="/t/:tenantSlug/my-consent/withdraw/:consentId" element={<DataOwnerWithdrawalForm />} />
          <Route path="/t/:tenantSlug/my-consent/withdrawals" element={<DataOwnerWithdrawalHistory />} />
          <Route path="/t/:tenantSlug/my-consent/help" element={<DataOwnerHelp />} />
          <Route path="/t/:tenantSlug/my-consent" element={<Navigate to="login" replace />} />
          
          {/* Tenant-slug based admin routes - shareable URLs */}
          <Route path="/t/:tenantSlug/dashboard" element={<ProtectedRoute><AdminRouteWrapper><Dashboard /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/templates" element={<ProtectedRoute><AdminRouteWrapper><Templates /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/template-library" element={<ProtectedRoute><AdminRouteWrapper><TemplateLibrary /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/consents" element={<ProtectedRoute><AdminRouteWrapper><ConsentRecords /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/data-collectors" element={<ProtectedRoute><AdminRouteWrapper><DataCollectors /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/fiduciaries" element={<ProtectedRoute><AdminRouteWrapper><DataCollectors /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/projects" element={<ProtectedRoute><AdminRouteWrapper><Projects /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/analytics" element={<ProtectedRoute><AdminRouteWrapper><Analytics /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/consent-forms" element={<ProtectedRoute><AdminRouteWrapper><ConsentForms /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/generate-url" element={<ProtectedRoute><AdminRouteWrapper><ConsentForms /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/tenant-profile" element={<ProtectedRoute><AdminRouteWrapper><TenantProfile /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/user-profile" element={<ProtectedRoute><AdminRouteWrapper><UserProfile /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/users" element={<ProtectedRoute><AdminRouteWrapper><UserManagement /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/audit-logs" element={<ProtectedRoute><AdminRouteWrapper><AuditLogs /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/withdrawals" element={<ProtectedRoute><AdminRouteWrapper><WithdrawalRequests /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/erasure" element={<ProtectedRoute><AdminRouteWrapper><DataErasure /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/help" element={<ProtectedRoute><AdminRouteWrapper><HelpSupport /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/signup-requests" element={<ProtectedRoute><AdminRouteWrapper><SignupRequests /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/notification-templates" element={<ProtectedRoute><AdminRouteWrapper><NotificationTemplateEditor /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/t/:tenantSlug/regions" element={<ProtectedRoute><AdminRouteWrapper><Regions /></AdminRouteWrapper></ProtectedRoute>} />
          
          {/* General Login (supports both slug and tenantId) */}
          <Route path="/login" element={<Login />} />
          
          {/* Organization Signup (new tenant requests) */}
          <Route path="/signup" element={<OrganizationSignupPage />} />
          
          {/* Accept Invitation (user onboarding) */}
          <Route path="/invite/accept" element={<AcceptInvitation />} />
          
          {/* Password Reset Routes */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Legacy /admin routes - maintained for backwards compatibility */}
          <Route path="/admin/dashboard" element={<ProtectedRoute><AdminRouteWrapper><Dashboard /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/templates" element={<ProtectedRoute><AdminRouteWrapper><Templates /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/template-library" element={<ProtectedRoute><AdminRouteWrapper><TemplateLibrary /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/consents" element={<ProtectedRoute><AdminRouteWrapper><ConsentRecords /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/data-collectors" element={<ProtectedRoute><AdminRouteWrapper><DataCollectors /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/fiduciaries" element={<ProtectedRoute><AdminRouteWrapper><DataCollectors /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/projects" element={<ProtectedRoute><AdminRouteWrapper><Projects /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute><AdminRouteWrapper><Analytics /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/consent-forms" element={<ProtectedRoute><AdminRouteWrapper><ConsentForms /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/generate-url" element={<ProtectedRoute><AdminRouteWrapper><ConsentForms /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/tenant-profile" element={<ProtectedRoute><AdminRouteWrapper><TenantProfile /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/user-profile" element={<ProtectedRoute><AdminRouteWrapper><UserProfile /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><AdminRouteWrapper><UserManagement /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/audit-logs" element={<ProtectedRoute><AdminRouteWrapper><AuditLogs /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/withdrawals" element={<ProtectedRoute><AdminRouteWrapper><WithdrawalRequests /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/erasure" element={<ProtectedRoute><AdminRouteWrapper><DataErasure /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/help" element={<ProtectedRoute><AdminRouteWrapper><HelpSupport /></AdminRouteWrapper></ProtectedRoute>} />
          
          <Route path="/admin/notification-templates" element={<ProtectedRoute><AdminRouteWrapper><NotificationTemplateEditor /></AdminRouteWrapper></ProtectedRoute>} />

          {/* Super Admin Routes (Platform Tenant Only) */}
          <Route path="/admin/signup-requests" element={<ProtectedRoute><AdminRouteWrapper><SignupRequests /></AdminRouteWrapper></ProtectedRoute>} />
          <Route path="/admin/regions" element={<ProtectedRoute><AdminRouteWrapper><Regions /></AdminRouteWrapper></ProtectedRoute>} />
          
          {/* Default Routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/" element={<HomePage />} />
        </Routes>
      </Router>
    </I18nProvider>
  );
}

export default App;
